import { useState, useMemo } from 'react';
import {
  Download,
  Save,
  Filter,
  Plus,
  Loader2,
  HelpCircle,
  BarChart3,
  Printer,
  Globe,
  MapPin,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';
import { useLocations } from '@/hooks/useLocations';
import { useCurrency } from '@/hooks/useCurrency';
import { format } from 'date-fns';
import { DateRangeSelect, type DateRangeValue } from '@/components/ui/date-range-select';
import { datePresets, presetValue } from '@/lib/datePresets';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import { useReportPrint } from '@/hooks/useReportPrint';

// NOTE: As products, categories, and features grow, this report should be extended to include:
// - Category-based reporting with hierarchy support
// - Product variants and modifiers breakdown
// - Supplier-based reporting
// - Customer segment analysis
// - Discount and promotion effectiveness
// - Tax breakdown by category
// - Payment method by product correlation
// - Time-based trends (week-over-week, month-over-month)
// The data queries and report types below are designed to be extensible.

type ReportTab = 'hourly' | 'performance' | 'summary' | 'charts';
type ReportType = 'product' | 'category' | 'employee' | 'register' | 'payment_method';
type MeasureType = 'items_sold' | 'gross_sales' | 'net_sales' | 'cost' | 'margin' | 'discounts' | 'returns' | 'tax';
type ComparisonType = 'none' | 'previous_period' | 'previous_year';

const DATE_RANGE_PRESETS = datePresets('today', 'yesterday', 'week', 'last30', 'month', 'last_month');

export default function SalesReports() {
  const { currentLocation } = useLocationStore();
  const { locations } = useLocations();
  const { formatCurrency } = useCurrency();

  const [selectedLocationId, setSelectedLocationId] = useState<string>(currentLocation?.id || 'all');
  const [activeTab, setActiveTab] = useState<ReportTab>('hourly');
  const [reportType, setReportType] = useState<ReportType>('product');

  // Determine which location to use
  const queryLocationId = selectedLocationId === 'all' ? currentLocation?.id : selectedLocationId;
  const selectedLocation = locations.find(l => l.id === selectedLocationId);
  const [measure, setMeasure] = useState<MeasureType>('items_sold');
  const [comparison, setComparison] = useState<ComparisonType>('none');
  const [dateSel, setDateSel] = useState<DateRangeValue>(() => presetValue('week'));
  const { printRef, handlePrint } = useReportPrint({ title: 'Sales report' });

  const dateFilters = useMemo(() => ({
    start: dateSel.start.toISOString(),
    end: dateSel.end.toISOString(),
  }), [dateSel]);

  // Fetch products for product-based report
  const { data: products } = useQuery({
    queryKey: ['products-list', queryLocationId],
    queryFn: async () => {
      if (!queryLocationId) return [];
      const { data, error } = await supabase
        .from('products')
        .select('id, name, sku, image_url, category_id, price, cost')
        .eq('location_id', queryLocationId)
        .eq('is_active', true);
      if (error) throw error;
      return data || [];
    },
    enabled: !!queryLocationId,
  });

  // Fetch categories for category-based report
  const { data: categories } = useQuery({
    queryKey: ['categories-list', queryLocationId],
    queryFn: async () => {
      if (!queryLocationId) return [];
      const { data, error } = await supabase
        .from('categories')
        .select('id, name')
        .eq('location_id', queryLocationId)
        .eq('is_active', true);
      if (error) throw error;
      return data || [];
    },
    enabled: !!queryLocationId,
  });

  // Fetch hourly sales data with product/category breakdown
  const { data: hourlyData, isLoading: hourlyLoading } = useQuery({
    queryKey: ['report-hourly-tabular', queryLocationId, dateFilters, reportType],
    queryFn: async () => {
      if (!queryLocationId) return { rows: [], hours: [] };

      const { data: items, error } = await supabase
        .from('order_items')
        .select(`
          id,
          name,
          quantity,
          total,
          unit_price,
          discount_amount,
          tax_amount,
          product_id,
          order:orders!inner(
            location_id,
            created_at,
            status,
            user_id,
            user:profiles(first_name, last_name)
          )
        `)
        .eq('order.location_id', queryLocationId)
        .gte('order.created_at', dateFilters.start)
        .lte('order.created_at', dateFilters.end)
        .in('order.status', ['completed', 'ready', 'sent']);

      if (error) throw error;

      // Build hourly breakdown based on report type
      const hourlyMap: Record<string, Record<number, {
        items_sold: number;
        gross_sales: number;
        net_sales: number;
        discounts: number;
        tax: number;
        cost: number;
      }>> = {};

      // Initialize hours 0-23
      const hours = Array.from({ length: 24 }, (_, i) => i);

      items?.forEach(item => {
        const order = item.order as any;
        const hour = new Date(order.created_at).getHours();

        let key: string;
        let displayName: string;
        let sku: string | undefined;
        let imageUrl: string | undefined;

        if (reportType === 'product') {
          key = item.product_id || item.name;
          const product = products?.find(p => p.id === item.product_id);
          displayName = product?.name || item.name;
          sku = product?.sku || undefined;
          imageUrl = product?.image_url || undefined;
        } else if (reportType === 'category') {
          const product = products?.find(p => p.id === item.product_id);
          const category = categories?.find(c => c.id === product?.category_id);
          key = product?.category_id || 'uncategorized';
          displayName = category?.name || 'Uncategorized';
        } else if (reportType === 'employee') {
          key = order.user_id || 'unknown';
          displayName = order.user
            ? `${order.user.first_name || ''} ${order.user.last_name || ''}`.trim() || 'Unknown'
            : 'Unknown';
        } else {
          key = item.name;
          displayName = item.name;
        }

        if (!hourlyMap[key]) {
          hourlyMap[key] = {};
          hours.forEach(h => {
            hourlyMap[key][h] = {
              items_sold: 0,
              gross_sales: 0,
              net_sales: 0,
              discounts: 0,
              tax: 0,
              cost: 0,
            };
          });
          // Store metadata
          (hourlyMap[key] as any)._meta = { displayName, sku, imageUrl };
        }

        const product = products?.find(p => p.id === item.product_id);
        const cost = (product?.cost || 0) * item.quantity;

        hourlyMap[key][hour].items_sold += item.quantity;
        hourlyMap[key][hour].gross_sales += item.total;
        hourlyMap[key][hour].net_sales += item.total - (item.tax_amount || 0);
        hourlyMap[key][hour].discounts += item.discount_amount || 0;
        hourlyMap[key][hour].tax += item.tax_amount || 0;
        hourlyMap[key][hour].cost += cost;
      });

      // Convert to rows
      const rows = Object.entries(hourlyMap).map(([key, hourData]) => {
        const meta = (hourData as any)._meta || { displayName: key };
        const totals = hours.reduce(
          (acc, h) => {
            acc.items_sold += hourData[h]?.items_sold || 0;
            acc.gross_sales += hourData[h]?.gross_sales || 0;
            acc.net_sales += hourData[h]?.net_sales || 0;
            acc.discounts += hourData[h]?.discounts || 0;
            acc.tax += hourData[h]?.tax || 0;
            acc.cost += hourData[h]?.cost || 0;
            return acc;
          },
          { items_sold: 0, gross_sales: 0, net_sales: 0, discounts: 0, tax: 0, cost: 0 }
        );

        return {
          id: key,
          name: meta.displayName,
          sku: meta.sku,
          imageUrl: meta.imageUrl,
          hourlyData: hourData,
          totals,
          margin: totals.gross_sales > 0 ? ((totals.gross_sales - totals.cost) / totals.gross_sales) * 100 : 0,
        };
      });

      // Sort by total items sold (descending)
      rows.sort((a, b) => b.totals.items_sold - a.totals.items_sold);

      return { rows, hours };
    },
    enabled: !!currentLocation?.id && !!products,
  });

  // Fetch employee performance data
  const { data: employeePerformance, isLoading: performanceLoading } = useQuery({
    queryKey: ['report-performance', queryLocationId, dateFilters],
    queryFn: async () => {
      if (!queryLocationId) return [];

      const { data: orders, error } = await supabase
        .from('orders')
        .select(`
          id,
          total,
          subtotal,
          tax_amount,
          discount_amount,
          created_at,
          user_id,
          user:profiles(first_name, last_name)
        `)
        .eq('location_id', queryLocationId)
        .gte('created_at', dateFilters.start)
        .lte('created_at', dateFilters.end)
        .in('status', ['completed', 'ready', 'sent']);

      if (error) throw error;

      // Group by user with hourly breakdown
      const userStats: Record<string, {
        name: string;
        hourlyData: Record<number, { orders: number; revenue: number }>;
        totals: { orders: number; revenue: number };
      }> = {};

      orders?.forEach(order => {
        const userId = order.user_id || 'unknown';
        const userName = order.user
          ? `${(order.user as any).first_name || ''} ${(order.user as any).last_name || ''}`.trim()
          : 'Unknown';
        const hour = new Date(order.created_at).getHours();

        if (!userStats[userId]) {
          userStats[userId] = {
            name: userName || 'Unknown',
            hourlyData: Object.fromEntries(Array.from({ length: 24 }, (_, i) => [i, { orders: 0, revenue: 0 }])),
            totals: { orders: 0, revenue: 0 },
          };
        }

        userStats[userId].hourlyData[hour].orders += 1;
        userStats[userId].hourlyData[hour].revenue += order.total || 0;
        userStats[userId].totals.orders += 1;
        userStats[userId].totals.revenue += order.total || 0;
      });

      return Object.entries(userStats)
        .map(([id, data]) => ({ id, ...data }))
        .sort((a, b) => b.totals.revenue - a.totals.revenue);
    },
    enabled: !!queryLocationId,
  });

  // Fetch summary stats
  const { data: summaryStats, isLoading: summaryLoading } = useQuery({
    queryKey: ['report-summary', queryLocationId, dateFilters],
    queryFn: async () => {
      if (!queryLocationId) return null;

      const { data: orders, error } = await supabase
        .from('orders')
        .select('id, total, subtotal, tax_amount, discount_amount, tip_amount, created_at, user_id, status')
        .eq('location_id', queryLocationId)
        .gte('created_at', dateFilters.start)
        .lte('created_at', dateFilters.end)
        .in('status', ['completed', 'ready', 'sent']);

      if (error) throw error;

      const totalRevenue = orders?.reduce((sum, o) => sum + (o.total || 0), 0) || 0;
      const totalOrders = orders?.length || 0;
      const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
      const totalTax = orders?.reduce((sum, o) => sum + (o.tax_amount || 0), 0) || 0;
      const totalDiscount = orders?.reduce((sum, o) => sum + (o.discount_amount || 0), 0) || 0;

      return {
        totalRevenue,
        totalOrders,
        avgOrderValue,
        totalTax,
        totalDiscount,
      };
    },
    enabled: !!queryLocationId,
  });

  // Fetch chart data (hourly aggregates)
  const { data: chartData, isLoading: chartLoading } = useQuery({
    queryKey: ['report-chart', queryLocationId, dateFilters],
    queryFn: async () => {
      if (!queryLocationId) return [];

      const { data: orders, error } = await supabase
        .from('orders')
        .select('total, created_at')
        .eq('location_id', queryLocationId)
        .gte('created_at', dateFilters.start)
        .lte('created_at', dateFilters.end)
        .in('status', ['completed', 'ready', 'sent']);

      if (error) throw error;

      // Group by hour
      const hourlyData: Record<number, { revenue: number; orders: number }> = {};
      for (let i = 0; i < 24; i++) {
        hourlyData[i] = { revenue: 0, orders: 0 };
      }

      orders?.forEach(order => {
        const hour = new Date(order.created_at).getHours();
        hourlyData[hour].revenue += order.total || 0;
        hourlyData[hour].orders += 1;
      });

      return Object.entries(hourlyData).map(([hour, data]) => ({
        hour: parseInt(hour),
        label: `${parseInt(hour) % 12 || 12}${parseInt(hour) < 12 ? 'am' : 'pm'}`,
        revenue: data.revenue,
        orders: data.orders,
      }));
    },
    enabled: !!queryLocationId,
  });

  const formatHourLabel = (hour: number) => {
    const nextHour = (hour + 1) % 24;
    const formatHour = (h: number) => `${h % 12 || 12}${h < 12 ? ' AM' : ' PM'}`;
    return `${formatHour(hour).replace(' ', '')}-${formatHour(nextHour).replace(' ', '')}`;
  };

  const getMeasureValue = (data: any, measureType: MeasureType): number | string => {
    switch (measureType) {
      case 'items_sold':
        return data.items_sold || 0;
      case 'gross_sales':
        return data.gross_sales || 0;
      case 'net_sales':
        return data.net_sales || 0;
      case 'discounts':
        return data.discounts || 0;
      case 'tax':
        return data.tax || 0;
      case 'cost':
        return data.cost || 0;
      case 'margin':
        return data.gross_sales > 0 ? ((data.gross_sales - data.cost) / data.gross_sales * 100).toFixed(1) + '%' : '0%';
      default:
        return 0;
    }
  };

  const formatMeasureValue = (value: number | string, measureType: MeasureType): string => {
    if (typeof value === 'string') return value;
    if (measureType === 'items_sold') return value.toString();
    if (measureType === 'margin') return `${value.toFixed(1)}%`;
    return formatCurrency(value);
  };

  const handleExport = () => {
    // Generate CSV based on current view
    let csvContent = '';

    if (activeTab === 'hourly' && hourlyData) {
      const headers = ['Product', 'SKU', ...hourlyData.hours.map(h => formatHourLabel(h)), 'Total'];
      csvContent = headers.join(',') + '\n';

      hourlyData.rows.forEach(row => {
        const values = [
          `"${row.name}"`,
          row.sku || '',
          ...hourlyData.hours.map(h => getMeasureValue(row.hourlyData[h], measure)),
          getMeasureValue(row.totals, measure),
        ];
        csvContent += values.join(',') + '\n';
      });
    }

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales-report-${format(dateSel.start, 'yyyy-MM-dd')}-to-${format(dateSel.end, 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  const dateRangeDisplay = `${format(dateSel.start, 'MMM d, yyyy')} to ${format(dateSel.end, 'MMM d, yyyy')}`;

  return (
    <div className="space-y-6 min-w-0 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold">Sales report</h1>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex items-center gap-6 border-b">
        {[
          { id: 'hourly', label: 'Sales by hour of day' },
          { id: 'performance', label: 'Individual performance' },
          { id: 'summary', label: 'Summary' },
          { id: 'charts', label: 'Charts' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as ReportTab)}
            className={cn(
              'px-1 py-3 text-sm font-medium transition-colors border-b-2 -mb-px',
              activeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Description and Save Button */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <p className="text-sm text-muted-foreground">
          {activeTab === 'hourly' && 'Get an overview of your busiest hours.'}
          {activeTab === 'performance' && 'Track individual employee sales performance.'}
          {activeTab === 'summary' && 'View summary statistics for the selected period.'}
          {activeTab === 'charts' && 'Visualize your sales data with charts.'}
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-2" onClick={handlePrint}>
            <Printer className="h-4 w-4" />
            Print
          </Button>
          <Button className="gap-2" size="sm">
            <Save className="h-4 w-4" />
            Save report
          </Button>
          <Button variant="ghost" size="icon" className="text-muted-foreground">
            <HelpCircle className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div>
          <label className="text-sm font-medium text-muted-foreground mb-2 block">Location</label>
          <Select value={selectedLocationId} onValueChange={setSelectedLocationId}>
            <SelectTrigger>
              <SelectValue placeholder="Select location" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                <div className="flex items-center gap-2">
                  <Globe className="h-3.5 w-3.5" />
                  <span>All locations</span>
                </div>
              </SelectItem>
              {locations.length > 0 && <div className="my-1 h-px bg-border" />}
              {locations.map((loc) => (
                <SelectItem key={loc.id} value={loc.id}>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>{loc.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-sm font-medium text-muted-foreground mb-2 block">Report type</label>
          <Select value={reportType} onValueChange={(v) => setReportType(v as ReportType)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="product">Product</SelectItem>
              <SelectItem value="category">Category</SelectItem>
              <SelectItem value="employee">Employee</SelectItem>
              <SelectItem value="register">Register</SelectItem>
              <SelectItem value="payment_method">Payment method</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-sm font-medium text-muted-foreground mb-2 block">Measure</label>
          <Select value={measure} onValueChange={(v) => setMeasure(v as MeasureType)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="items_sold">Items sold</SelectItem>
              <SelectItem value="gross_sales">Gross sales</SelectItem>
              <SelectItem value="net_sales">Net sales</SelectItem>
              <SelectItem value="cost">Cost</SelectItem>
              <SelectItem value="margin">Margin</SelectItem>
              <SelectItem value="discounts">Discounts</SelectItem>
              <SelectItem value="tax">Tax</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-sm font-medium text-muted-foreground mb-2 block">Date range</label>
          <DateRangeSelect
            presets={DATE_RANGE_PRESETS}
            value={dateSel}
            onChange={setDateSel}
            className="w-full"
          />
          <p className="mt-1 text-xs text-muted-foreground">{dateRangeDisplay}</p>
        </div>

        <div>
          <label className="text-sm font-medium text-muted-foreground mb-2 block">Comparison</label>
          <Select value={comparison} onValueChange={(v) => setComparison(v as ComparisonType)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No comparison</SelectItem>
              <SelectItem value="previous_period">Previous period</SelectItem>
              <SelectItem value="previous_year">Previous year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* More Filters Link */}
      <div className="flex justify-end">
        <button className="text-sm text-primary hover:underline">
          More filters
        </button>
      </div>

      {/* Action Bar */}
      <div className="flex items-center justify-between border-y py-3">
        <Button variant="ghost" size="sm" className="gap-2">
          <Filter className="h-4 w-4" />
          Format results
        </Button>
        <Button variant="ghost" size="sm" className="gap-2" onClick={handleExport}>
          <Download className="h-4 w-4" />
          Export report...
        </Button>
      </div>

      {/* Sales by Hour Tab - Tabular Data */}
      {activeTab === 'hourly' && (
        <div className="border rounded-lg overflow-hidden max-w-full">
          {hourlyLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <div className="overflow-x-auto max-w-full">
              <table className="text-sm min-w-max">
                <thead>
                  <tr>
                    <th className="text-left py-3 px-4 font-medium sticky left-0 z-20 min-w-[200px] bg-muted shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                      <div className="flex items-center gap-2">
                        {reportType === 'product' ? 'Product' : reportType === 'category' ? 'Category' : 'Employee'}
                        <button className="text-primary">
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    </th>
                    {hourlyData?.hours.map((hour) => (
                      <th key={hour} className="text-right py-3 px-3 font-medium whitespace-nowrap min-w-[80px] bg-muted/50">
                        {formatHourLabel(hour)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {hourlyData?.rows.map((row, idx) => (
                    <tr key={row.id} className={cn('border-t', idx % 2 === 0 ? 'bg-background' : 'bg-muted/20')}>
                      <td className={cn(
                        "py-3 px-4 sticky left-0 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]",
                        idx % 2 === 0 ? 'bg-background' : 'bg-muted'
                      )}>
                        <div className="flex items-center gap-3">
                          {row.imageUrl ? (
                            <img
                              src={row.imageUrl}
                              alt={row.name}
                              className="w-8 h-8 rounded object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                              <BarChart3 className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                          <div>
                            <a href="#" className="text-primary hover:underline font-medium">
                              {row.name}
                            </a>
                            {row.sku && (
                              <p className="text-xs text-muted-foreground">{row.sku}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      {hourlyData.hours.map((hour) => {
                        const value = getMeasureValue(row.hourlyData[hour], measure);
                        return (
                          <td key={hour} className="py-3 px-3 text-right tabular-nums">
                            {typeof value === 'number' && value === 0 ? (
                              <span className="text-muted-foreground">0</span>
                            ) : (
                              formatMeasureValue(value, measure)
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {(!hourlyData?.rows || hourlyData.rows.length === 0) && (
                    <tr>
                      <td colSpan={25} className="py-8 text-center text-muted-foreground">
                        No data available for the selected period
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Individual Performance Tab */}
      {activeTab === 'performance' && (
        <div className="border rounded-lg overflow-hidden max-w-full">
          {performanceLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <div className="overflow-x-auto max-w-full">
              <table className="text-sm min-w-max">
                <thead>
                  <tr>
                    <th className="text-left py-3 px-4 font-medium sticky left-0 z-20 min-w-[200px] bg-muted shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                      Employee
                    </th>
                    {Array.from({ length: 24 }, (_, i) => i).map((hour) => (
                      <th key={hour} className="text-right py-3 px-3 font-medium whitespace-nowrap min-w-[80px] bg-muted/50">
                        {formatHourLabel(hour)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {employeePerformance?.map((emp, idx) => (
                    <tr key={emp.id} className={cn('border-t', idx % 2 === 0 ? 'bg-background' : 'bg-muted/20')}>
                      <td className={cn(
                        "py-3 px-4 sticky left-0 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]",
                        idx % 2 === 0 ? 'bg-background' : 'bg-muted'
                      )}>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-sm font-medium text-primary">
                              {emp.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium">{emp.name}</span>
                            <p className="text-xs text-muted-foreground">
                              {emp.totals.orders} orders
                            </p>
                          </div>
                        </div>
                      </td>
                      {Array.from({ length: 24 }, (_, hour) => {
                        const data = emp.hourlyData[hour];
                        const value = measure === 'items_sold' ? data?.orders || 0 : data?.revenue || 0;
                        return (
                          <td key={hour} className="py-3 px-3 text-right tabular-nums">
                            {value === 0 ? (
                              <span className="text-muted-foreground">0</span>
                            ) : measure === 'items_sold' ? (
                              value
                            ) : (
                              formatCurrency(value)
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {(!employeePerformance || employeePerformance.length === 0) && (
                    <tr>
                      <td colSpan={25} className="py-8 text-center text-muted-foreground">
                        No employee data available for the selected period
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Summary Tab */}
      {activeTab === 'summary' && (
        <div className="space-y-6">
          {summaryLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">Gross Sales</p>
                    <p className="text-2xl font-bold mt-1">
                      {formatCurrency(summaryStats?.totalRevenue || 0)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">Net Sales</p>
                    <p className="text-2xl font-bold mt-1">
                      {formatCurrency((summaryStats?.totalRevenue || 0) - (summaryStats?.totalTax || 0))}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">Total Orders</p>
                    <p className="text-2xl font-bold mt-1">
                      {summaryStats?.totalOrders || 0}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">Avg Order Value</p>
                    <p className="text-2xl font-bold mt-1">
                      {formatCurrency(summaryStats?.avgOrderValue || 0)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">Total Tax</p>
                    <p className="text-2xl font-bold mt-1">
                      {formatCurrency(summaryStats?.totalTax || 0)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Summary by {reportType === 'product' ? 'Product' : reportType === 'category' ? 'Category' : 'Employee'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left py-3 px-4">Name</th>
                          <th className="text-right py-3 px-4">Items Sold</th>
                          <th className="text-right py-3 px-4">Gross Sales</th>
                          <th className="text-right py-3 px-4">Net Sales</th>
                          <th className="text-right py-3 px-4">Tax</th>
                          <th className="text-right py-3 px-4">% of Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hourlyData?.rows.slice(0, 20).map((row) => {
                          const totalRevenue = hourlyData.rows.reduce((sum, r) => sum + r.totals.gross_sales, 0);
                          const percentage = totalRevenue > 0 ? (row.totals.gross_sales / totalRevenue) * 100 : 0;

                          return (
                            <tr key={row.id} className="border-b">
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-3">
                                  {row.imageUrl && (
                                    <img src={row.imageUrl} alt="" className="w-8 h-8 rounded object-cover" />
                                  )}
                                  <div>
                                    <span className="font-medium">{row.name}</span>
                                    {row.sku && <p className="text-xs text-muted-foreground">{row.sku}</p>}
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-4 text-right tabular-nums">{row.totals.items_sold}</td>
                              <td className="py-3 px-4 text-right tabular-nums">{formatCurrency(row.totals.gross_sales)}</td>
                              <td className="py-3 px-4 text-right tabular-nums">{formatCurrency(row.totals.net_sales)}</td>
                              <td className="py-3 px-4 text-right tabular-nums">{formatCurrency(row.totals.tax)}</td>
                              <td className="py-3 px-4 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-primary rounded-full"
                                      style={{ width: `${Math.min(percentage, 100)}%` }}
                                    />
                                  </div>
                                  <span className="w-12 text-right tabular-nums">{percentage.toFixed(1)}%</span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

      {/* Charts Tab */}
      {activeTab === 'charts' && (
        <div className="space-y-6">
          {chartLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Revenue by Hour</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[400px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" fontSize={12} />
                        <YAxis fontSize={12} tickFormatter={(v) => formatCurrency(v)} />
                        <Tooltip
                          formatter={(value: number) => formatCurrency(value)}
                          labelFormatter={(label) => `Time: ${label}`}
                        />
                        <Bar dataKey="revenue" fill="#f97316" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Orders by Hour</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[400px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" fontSize={12} />
                        <YAxis fontSize={12} />
                        <Tooltip
                          formatter={(value: number) => [value, 'Orders']}
                          labelFormatter={(label) => `Time: ${label}`}
                        />
                        <Line
                          type="monotone"
                          dataKey="orders"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          dot={true}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Top Products</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[400px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={hourlyData?.rows.slice(0, 10).map(r => ({
                          name: r.name.length > 20 ? r.name.slice(0, 20) + '...' : r.name,
                          quantity: r.totals.items_sold,
                          revenue: r.totals.gross_sales,
                        })) || []}
                        layout="vertical"
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" fontSize={12} />
                        <YAxis dataKey="name" type="category" fontSize={12} width={150} />
                        <Tooltip
                          formatter={(value: number, name: string) => [
                            name === 'quantity' ? `${value} sold` : formatCurrency(value),
                            name === 'quantity' ? 'Quantity' : 'Revenue',
                          ]}
                        />
                        <Bar dataKey="quantity" fill="#f97316" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

      {/* Print template (hidden in UI, used for structured printing) */}
      <div ref={printRef} className="print-source">
        <div className="print-header">
          <div>
            <h1>Sales report</h1>
            <div className="print-meta">
              {selectedLocation?.name || currentLocation?.name ? `${selectedLocation?.name || currentLocation?.name} • ` : ''}{dateRangeDisplay}
            </div>
            <div className="print-meta">
              Breakdown: {reportType} • Measure: {measure.replace('_', ' ')} • Comparison: {comparison.replace('_', ' ')}
            </div>
          </div>
          <div className="print-meta">
            Generated on {format(new Date(), 'MMM d, yyyy p')}
          </div>
        </div>

        {activeTab === 'hourly' && (
          <div className="print-section">
            <h2>Sales by hour</h2>
            {hourlyData?.rows?.length ? (
              <table className="print-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    {hourlyData.hours.map((hour) => (
                      <th key={hour}>{formatHourLabel(hour)}</th>
                    ))}
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {hourlyData.rows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <div>
                          <div>{row.name}</div>
                          {row.sku && <div className="print-meta">SKU: {row.sku}</div>}
                        </div>
                      </td>
                      {hourlyData.hours.map((hour) => (
                        <td key={hour}>
                          {formatMeasureValue(getMeasureValue(row.hourlyData[hour], measure), measure)}
                        </td>
                      ))}
                      <td>{formatMeasureValue(getMeasureValue(row.totals, measure), measure)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="print-meta">No data for this period.</div>
            )}
          </div>
        )}

        {activeTab === 'performance' && (
          <div className="print-section">
            <h2>Employee performance</h2>
            {employeePerformance?.length ? (
              <table className="print-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Orders</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {employeePerformance.map(emp => (
                    <tr key={emp.id}>
                      <td>{emp.name}</td>
                      <td>{emp.totals.orders}</td>
                      <td>{formatCurrency(emp.totals.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="print-meta">No employee data for this period.</div>
            )}
          </div>
        )}

        {activeTab === 'summary' && (
          <div className="print-section">
            <h2>Summary</h2>
            {summaryStats ? (
              <div className="print-summary">
                <div className="print-card">
                  <div className="label">Gross sales</div>
                  <div className="value">{formatCurrency(summaryStats.totalRevenue || 0)}</div>
                </div>
                <div className="print-card">
                  <div className="label">Net sales</div>
                  <div className="value">
                    {formatCurrency((summaryStats.totalRevenue || 0) - (summaryStats.totalTax || 0))}
                  </div>
                </div>
                <div className="print-card">
                  <div className="label">Total orders</div>
                  <div className="value">{summaryStats.totalOrders || 0}</div>
                </div>
                <div className="print-card">
                  <div className="label">Avg order value</div>
                  <div className="value">{formatCurrency(summaryStats.avgOrderValue || 0)}</div>
                </div>
                <div className="print-card">
                  <div className="label">Total tax</div>
                  <div className="value">{formatCurrency(summaryStats.totalTax || 0)}</div>
                </div>
              </div>
            ) : (
              <div className="print-meta">No summary available.</div>
            )}

            {hourlyData?.rows?.length ? (
              <div className="print-section">
                <h3>Top performers</h3>
                <table className="print-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Items sold</th>
                      <th>Gross sales</th>
                      <th>Net sales</th>
                      <th>Tax</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hourlyData.rows.slice(0, 20).map(row => (
                      <tr key={row.id}>
                        <td>
                          <div>{row.name}</div>
                          {row.sku && <div className="print-meta">SKU: {row.sku}</div>}
                        </td>
                        <td>{row.totals.items_sold}</td>
                        <td>{formatCurrency(row.totals.gross_sales)}</td>
                        <td>{formatCurrency(row.totals.net_sales)}</td>
                        <td>{formatCurrency(row.totals.tax)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        )}

        {activeTab === 'charts' && (
          <div className="print-section">
            <h2>Revenue by hour (table)</h2>
            {chartData?.length ? (
              <table className="print-table">
                <thead>
                  <tr>
                    <th>Hour</th>
                    <th>Revenue</th>
                    <th>Orders</th>
                  </tr>
                </thead>
                <tbody>
                  {chartData.map((row) => (
                    <tr key={row.hour}>
                      <td>{row.label}</td>
                      <td>{formatCurrency(row.revenue)}</td>
                      <td>{row.orders}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="print-meta">No chart data for this period.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
